import { USE_MOCKS, api, getActiveEntityId, mockDelay } from "@/lib/api"
import type { CambioCuenta, PlanGerencial } from "@/routes/masters/plan"
import type {
  Client,
  Contract,
  CostCenter,
  Person,
  Supplier,
  TreatmentRule,
} from "@/lib/types"
import type {
  ContractRow,
  CostCenterRow,
  PartyRow,
  PersonRow,
  TreatmentRuleRow,
} from "@/routes/masters/mapping"
import {
  clientFromApi,
  clientToApi,
  conMotivo,
  contractFromApi,
  contractToApi,
  costCenterFromApi,
  costCenterToApi,
  personFromApi,
  personToApi,
  supplierFromApi,
  supplierToApi,
  treatmentRuleFromApi,
  treatmentRuleToApi,
} from "@/routes/masters/mapping"

/**
 * Maestros y politicas. Es la capa que el usuario configura sin ingenieros.
 *
 *   GET/POST/PATCH /api/masters/clients | suppliers | contracts | people | cost-centers
 *   GET/POST/PATCH /api/masters/policies/collection | policies/payment
 *   GET/POST      /api/masters/treatment-rules (+ /{id}/replace)
 *
 * Toda escritura lleva `reason` obligatorio y queda versionada (autor, fecha, motivo).
 *
 * Este archivo NO traduce: solo decide ruta y verbo, y delega la forma del
 * cuerpo y de la respuesta a `mapping.ts`, que es donde vive la tabla de
 * equivalencias entre el modelo de UI (ingles) y el de datos (espanol). El
 * modo `USE_MOCKS` sigue intacto y devuelve los tipos de la SPA sin pasar por
 * el mapeo, que es justamente lo que lo hace util como control.
 */

export interface WithReason<T> {
  data: T
  reason: string
}

/**
 * Filtro por sociedad para los LISTADOS que lo aceptan.
 *
 * `entity_id` es opcional en estos GET, y esa es justamente la trampa: sin el,
 * el backend devuelve las filas de TODAS las sociedades del cliente mezcladas,
 * y la pantalla muestra un total que no es de ninguna. No falla, que es peor
 * que fallar. Las pantallas que llaman a estas funciones estan detras de
 * `<RequireEntity>`, asi que aca siempre hay entidad; el `?? undefined` es
 * cinturon, no tirante.
 */
function deLaEntidadActiva() {
  return { query: { entity_id: getActiveEntityId() ?? undefined } }
}

const MOCK_CLIENTS: Client[] = [
  {
    id: "cli-001",
    name: "Nexo Digital SAC",
    ruc: "20601112223",
    country: "PE",
    currency: "PEN",
    paymentTermsDays: 30,
    collectionEmail: "cuentasporpagar@ejemplo.com",
    activo: true,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "cli-002",
    name: "Retail Andes SA",
    ruc: "20554443332",
    country: "PE",
    currency: "PEN",
    paymentTermsDays: 60,
    collectionEmail: "cuentasporpagar@ejemplo.com",
    activo: true,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "cli-003",
    name: "Globant LatAm Corp",
    ruc: "30712345678",
    country: "US",
    currency: "USD",
    paymentTermsDays: 45,
    collectionEmail: "cuentasporpagar@ejemplo.com",
    activo: true,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "cli-004",
    name: "Clinica Santa Rosa EIRL",
    ruc: "20512223334",
    country: "PE",
    currency: "PEN",
    paymentTermsDays: 30,
    collectionEmail: "cuentasporpagar@ejemplo.com",
    activo: true,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
]

const MOCK_SUPPLIERS: Supplier[] = [
  {
    id: "prv-001",
    name: "Servicios Cloud Andina SAC",
    ruc: "20512345678",
    currency: "PEN",
    paymentTermsDays: 15,
    retentionApplies: false,
    activo: true,
    modalidadGasto: null,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "prv-002",
    name: "Transportes Vega SAC",
    ruc: "20100047218",
    currency: "PEN",
    paymentTermsDays: 30,
    retentionApplies: true,
    detractionCode: "027",
    detractionRate: 0.04,
    activo: true,
    modalidadGasto: null,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "prv-003",
    name: "Estudio Legal Paredes & Asoc.",
    ruc: "20601234567",
    currency: "USD",
    paymentTermsDays: 30,
    retentionApplies: false,
    activo: true,
    modalidadGasto: null,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "prv-004",
    name: "Consultora Talento Plus SAC",
    ruc: "20478899001",
    currency: "PEN",
    paymentTermsDays: 45,
    retentionApplies: true,
    detractionCode: "022",
    detractionRate: 0.12,
    activo: true,
    modalidadGasto: null,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
  {
    id: "prv-005",
    name: "Energia del Peru SA",
    ruc: "20100128056",
    currency: "PEN",
    paymentTermsDays: 15,
    retentionApplies: false,
    activo: true,
    modalidadGasto: null,
    enlaceContrato: "",
    diaDePago: null,
    contactos: 0,
    lineaHoja: "",
  },
]

const MOCK_CONTRACTS: Contract[] = [
  {
    id: "ctr-001",
    clientId: "cli-001",
    clientName: "Nexo Digital SAC",
    code: "NEX-2026-01",
    billingModel: "fixed_recurring",
    currency: "PEN",
    indexation: "ipc",
    amount: 28000,
    startDate: "2026-01-01",
    endDate: "2026-12-31",
    renewal: "automatica",
    renewalProbability: 0.85,
    lines: [{
      id: "ln-001", description: "NEX-2026-01", billingModel: "fixed_recurring",
      amount: 28000, quantity: 1, frequency: "mensual",
    }],
    status: "activo",
  },
  {
    id: "ctr-002",
    clientId: "cli-002",
    clientName: "Retail Andes SA",
    code: "RET-2026-04",
    billingModel: "milestone",
    currency: "PEN",
    indexation: "ninguna",
    amount: 180000,
    startDate: "2026-04-01",
    endDate: "2026-11-30",
    renewal: "manual",
    renewalProbability: 0.4,
    paymentTermsDays: 45,
    // Dos lineas a proposito: es el caso que la pantalla no sabia leer.
    lines: [
      { id: "ln-002a", description: "Hito 1 - diseno", billingModel: "milestone",
        amount: 80000, quantity: 1 },
      { id: "ln-002b", description: "Hito 2 - implantacion", billingModel: "milestone",
        amount: 100000, quantity: 1 },
    ],
    status: "activo",
  },
  {
    id: "ctr-003",
    clientId: "cli-003",
    clientName: "Globant LatAm Corp",
    code: "GLB-2026-02",
    billingModel: "time_and_materials",
    currency: "USD",
    indexation: "usd",
    amount: 42000,
    startDate: "2026-02-15",
    endDate: "2027-02-14",
    renewal: "automatica",
    renewalProbability: 0.7,
    lines: [{
      id: "ln-003", description: "Bolsa de horas", billingModel: "time_and_materials",
      amount: 3500, quantity: 12,
    }],
    status: "activo",
  },
  {
    id: "ctr-004",
    clientId: "cli-004",
    clientName: "Clinica Santa Rosa EIRL",
    code: "CSR-2025-09",
    billingModel: "fixed_recurring",
    currency: "PEN",
    indexation: "ninguna",
    amount: 9500,
    startDate: "2025-09-01",
    endDate: "2026-08-31",
    renewal: "manual",
    renewalProbability: 0.5,
    lines: [{
      id: "ln-004", description: "CSR-2025-09", billingModel: "fixed_recurring",
      amount: 9500, quantity: 1, frequency: "mensual",
    }],
    status: "activo",
  },
]

const MOCK_PEOPLE: Person[] = [
  {
    id: "per-001",
    entityId: "e-001",
    entityName: "Andina Servicios SAC",
    entityCountry: "PE",
    fullName: "Lucia Ramos Delgado",
    docNumber: "45889012",
    position: "Lider de proyecto",
    costCenterId: "cc-ope",
    costCenterName: "Operaciones / Delivery",
    nature: "costo_venta",
    behavior: "fijo",
    regime: "planilla",
    billable: true,
    clientIds: ["cli-001", "cli-002"],
    startDate: "2024-03-01",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "B3",
    monthlyGross: 8500,
    plannedRaise: "2026-10 · +8%",
    status: "activo",
  },
  {
    id: "per-002",
    entityId: "e-002",
    entityName: "Andina Logistica SAC",
    entityCountry: "PE",
    fullName: "Marco Antonio Silva",
    docNumber: "41225678",
    position: "Desarrollador senior",
    costCenterId: "cc-tec",
    costCenterName: "Tecnologia",
    nature: "costo_venta",
    behavior: "fijo",
    regime: "planilla",
    billable: true,
    clientIds: ["cli-003"],
    startDate: "2023-07-15",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "B4",
    monthlyGross: 11000,
    status: "activo",
  },
  {
    id: "per-003",
    entityId: "e-001",
    entityName: "Andina Servicios SAC",
    entityCountry: "PE",
    fullName: "Carla Espinoza Vera",
    docNumber: "70112233",
    position: "Analista de administracion",
    costCenterId: "cc-adm",
    costCenterName: "Administracion",
    nature: "gasto_operativo",
    behavior: "fijo",
    regime: "planilla",
    billable: false,
    clientIds: [],
    startDate: "2025-01-06",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "B2",
    monthlyGross: 4200,
    status: "activo",
  },
  {
    id: "per-004",
    entityId: "e-002",
    entityName: "Andina Logistica SAC",
    entityCountry: "PE",
    fullName: "Jorge Luis Ballon",
    docNumber: "09887766",
    position: "Consultor tributario",
    costCenterId: "cc-adm",
    costCenterName: "Administracion",
    nature: "gasto_operativo",
    behavior: "variable",
    regime: "recibo_honorarios",
    billable: false,
    clientIds: [],
    startDate: "2025-06-01",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "RxH",
    monthlyGross: 3500,
    status: "activo",
  },
  {
    id: "per-005",
    entityId: "e-001",
    entityName: "Andina Servicios SAC",
    entityCountry: "PE",
    fullName: "Andrea Chavez Nunez",
    docNumber: "76554433",
    position: "Disenadora UX",
    costCenterId: "cc-ope",
    costCenterName: "Operaciones / Delivery",
    nature: "costo_venta",
    behavior: "semi_variable",
    regime: "recibo_honorarios",
    billable: true,
    clientIds: ["cli-001"],
    startDate: "2026-02-01",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "RxH",
    monthlyGross: 6000,
    status: "activo",
  },
  {
    id: "per-006",
    entityId: "e-002",
    entityName: "Andina Logistica SAC",
    entityCountry: "PE",
    fullName: "Renzo Palacios Ruiz",
    docNumber: "44332211",
    position: "Ingeniero de plataforma",
    costCenterId: "cc-tec",
    costCenterName: "Tecnologia",
    nature: "capex",
    behavior: "fijo",
    regime: "planilla",
    billable: false,
    clientIds: [],
    startDate: "2026-05-02",
    endDate: "",
    email: "",
    lineaHoja: "",
    currency: "PEN",
    salaryBand: "B4",
    monthlyGross: 12000,
    plannedRaise: "2027-01 · +6%",
    status: "activo",
  },
]

const MOCK_COST_CENTERS: CostCenter[] = [
  {
    id: "cc-adm",
    code: "CC-ADM",
    name: "Administracion",
    area: "Soporte",
    defaultNature: "gasto_operativo",
    status: "activo",
  },
  {
    id: "cc-com",
    code: "CC-COM",
    name: "Comercial",
    area: "Comercial",
    defaultNature: "gasto_operativo",
    status: "activo",
  },
  {
    id: "cc-ope",
    code: "CC-OPE",
    name: "Operaciones / Delivery",
    area: "Delivery",
    defaultNature: "costo_venta",
    status: "activo",
  },
  {
    id: "cc-tec",
    code: "CC-TEC",
    name: "Tecnologia",
    area: "Producto",
    defaultNature: "costo_venta",
    status: "activo",
  },
  {
    id: "cc-inn",
    code: "CC-INN",
    name: "Innovacion / I+D",
    area: "Producto",
    defaultNature: "capex",
    status: "activo",
  },
]

const MOCK_TREATMENT_RULES: TreatmentRule[] = [
  {
    id: "TR-014",
    name: "Multas y sanciones",
    scopeType: "cuenta",
    scopeValue: "65911",
    statutory: { action: "incluir", note: "Se registra el gasto integro." },
    tax: {
      action: "adicion",
      legalBasis: "Art. 44 inc. c LIR",
      note: "No deducible: adicion en la determinacion.",
    },
    management: {
      action: "excluir",
      excludeFromEbitda: true,
      note: "No es operativo: fuera del P&L gerencial.",
    },
    effectiveFrom: "2026-01-01",
    author: "Rosa Quispe",
    reason: "Criterio del estudio: las multas no reflejan desempeno operativo.",
    updatedAt: "2026-01-05T10:00:00Z",
    status: "vigente",
  },
  {
    id: "TR-007",
    name: "Gastos de representacion",
    scopeType: "cuenta",
    scopeValue: "63711",
    statutory: { action: "incluir" },
    tax: {
      action: "limite",
      limitPct: 0.005,
      legalBasis: "Art. 37 inc. q LIR — 0.5% ingresos brutos, tope 40 UIT",
    },
    management: { action: "incluir", note: "Gasto real de caja, integro." },
    effectiveFrom: "2026-01-01",
    author: "Rosa Quispe",
    reason: "Semilla del rulepack peruano.",
    updatedAt: "2026-01-05T10:00:00Z",
    status: "vigente",
  },
  {
    id: "TR-021",
    name: "Sueldo del socio bajo mercado",
    scopeType: "condicion",
    scopeValue: "persona.esSocio = true",
    statutory: { action: "incluir", note: "Al valor efectivamente pagado." },
    tax: { action: "incluir", note: "Al valor pagado." },
    management: {
      action: "normalizar",
      normalizedValue: 14000,
      note: "Se normaliza a banda de mercado para ver el margen real.",
    },
    effectiveFrom: "2026-03-01",
    author: "Diego Fuentes (CFO)",
    reason: "El sueldo pagado distorsiona el margen operativo del area.",
    updatedAt: "2026-03-02T12:00:00Z",
    status: "vigente",
  },
  {
    id: "TR-032",
    name: "Bono extraordinario del equipo",
    scopeType: "etiqueta",
    scopeValue: "bono_extraordinario",
    statutory: { action: "incluir" },
    tax: { action: "incluir", legalBasis: "Art. 37 inc. l LIR (deducible al pago)" },
    management: {
      action: "incluir",
      excludeFromEbitda: true,
      note: "Dentro del P&L, fuera del EBITDA ajustado.",
    },
    effectiveFrom: "2026-06-01",
    author: "Diego Fuentes (CFO)",
    reason: "Es real pero no recurrente: no debe ensuciar el EBITDA comparable.",
    updatedAt: "2026-06-01T09:30:00Z",
    status: "vigente",
  },
  {
    id: "TR-040",
    name: "Honorarios de constitucion",
    scopeType: "proveedor",
    scopeValue: "20601234567",
    statutory: { action: "importe_con_limite", note: "Se activa y amortiza." },
    tax: { action: "deduccion", legalBasis: "Amortizacion en 1 o 10 anos (art. 37 inc. g LIR)" },
    management: { action: "reclasificar", targetAccount: "65911", note: "Gasto de una sola vez." },
    effectiveFrom: "2026-09-01",
    author: "Rosa Quispe",
    reason: "Distinto tratamiento contable y gerencial del gasto de constitucion.",
    updatedAt: "2026-08-10T16:20:00Z",
    status: "programada",
  },
]

/* ---------- Clientes ---------- */

export async function fetchClients(): Promise<Client[]> {
  if (USE_MOCKS) return mockDelay(MOCK_CLIENTS)
  const rows = await api.get<PartyRow[]>("/masters/clients")
  return rows.map((row) => clientFromApi(row))
}

export async function saveClient(payload: WithReason<Partial<Client> & { id?: string }>) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(clientToApi(payload.data), payload.reason)
  if (payload.data.id) return api.patch(`/masters/clients/${payload.data.id}`, body)
  return api.post("/masters/clients", body)
}

/* ---------- Proveedores ---------- */

export async function fetchSuppliers(): Promise<Supplier[]> {
  if (USE_MOCKS) return mockDelay(MOCK_SUPPLIERS)
  const rows = await api.get<PartyRow[]>("/masters/suppliers")
  return rows.map((row) => supplierFromApi(row))
}

export async function saveSupplier(payload: WithReason<Partial<Supplier> & { id?: string }>) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(supplierToApi(payload.data), payload.reason)
  if (payload.data.id) return api.patch(`/masters/suppliers/${payload.data.id}`, body)
  return api.post("/masters/suppliers", body)
}

/* ---------- Contratos ---------- */

/* ---------- Contactos de una parte (migración 0035) ---------- */

export interface Contacto {
  id: string
  nombre: string
  email: string | null
  telefono: string | null
  cargo: string | null
  principal: boolean
}

export async function fetchContactos(partyId: string): Promise<Contacto[]> {
  if (USE_MOCKS) return mockDelay([])
  return api.get<Contacto[]>(`/masters/partes/${partyId}/contactos`)
}

export async function crearContacto(partyId: string, c: {
  nombre: string; email?: string; telefono?: string; cargo?: string; principal?: boolean
}) {
  if (USE_MOCKS) return mockDelay({ id: "ct-nuevo" })
  return api.post(`/masters/partes/${partyId}/contactos`, {
    ...c, email: c.email || null, telefono: c.telefono || null, cargo: c.cargo || null,
    motivo: "Contacto añadido desde el maestro.",
  })
}

export async function borrarContacto(partyId: string, contactoId: string) {
  if (USE_MOCKS) return mockDelay({ id: contactoId, borrado: true })
  return api.delete(`/masters/partes/${partyId}/contactos/${contactoId}`)
}

export async function fetchContracts(): Promise<Contract[]> {
  if (USE_MOCKS) return mockDelay(MOCK_CONTRACTS)
  const rows = await api.get<ContractRow[]>("/masters/contracts", deLaEntidadActiva())
  return rows.map(contractFromApi)
}

export async function saveContract(payload: WithReason<Partial<Contract> & { id?: string }>) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(contractToApi(payload.data), payload.reason)
  if (payload.data.id) return api.patch(`/masters/contracts/${payload.data.id}`, body)
  return api.post("/masters/contracts", body)
}

/* ---------- Personas ---------- */

export async function fetchPeople(): Promise<Person[]> {
  if (USE_MOCKS) return mockDelay(MOCK_PEOPLE)
  // `GET /masters/people` devuelve el CODIGO del centro de costo, no su uuid;
  // el listado de centros lo resuelve para que el formulario pueda preseleccionar.
  const [rows, costCenters] = await Promise.all([
    api.get<PersonRow[]>("/masters/people", deLaEntidadActiva()),
    api.get<CostCenterRow[]>("/masters/cost-centers", deLaEntidadActiva()),
  ])
  return rows.map((row) => personFromApi(row, { costCenters }))
}

export async function savePerson(payload: WithReason<Partial<Person> & { id?: string }>) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(personToApi(payload.data), payload.reason)
  if (payload.data.id) return api.patch(`/masters/people/${payload.data.id}`, body)
  return api.post("/masters/people", body)
}

/* ---------- Centros de costo ---------- */

export async function fetchCostCenters(): Promise<CostCenter[]> {
  if (USE_MOCKS) return mockDelay(MOCK_COST_CENTERS)
  const rows = await api.get<CostCenterRow[]>("/masters/cost-centers", deLaEntidadActiva())
  return rows.map(costCenterFromApi)
}

export async function saveCostCenter(payload: WithReason<Partial<CostCenter> & { id?: string }>) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(costCenterToApi(payload.data), payload.reason)
  if (payload.data.id) return api.patch(`/masters/cost-centers/${payload.data.id}`, body)
  return api.post("/masters/cost-centers", body)
}

/* ---------- Politicas ---------- */

/* ---------- Reglas de tratamiento ---------- */

export async function fetchTreatmentRules(): Promise<TreatmentRule[]> {
  if (USE_MOCKS) return mockDelay(MOCK_TREATMENT_RULES)
  const rows = await api.get<TreatmentRuleRow[]>("/masters/treatment-rules", deLaEntidadActiva())
  return rows.map((row) => treatmentRuleFromApi(row))
}

export async function saveTreatmentRule(
  payload: WithReason<Partial<TreatmentRule> & { id?: string }>,
) {
  if (USE_MOCKS) return mockDelay(payload)
  const body = conMotivo(treatmentRuleToApi(payload.data), payload.reason)
  // Cada cambio es una regla nueva con vigencia, no una edicion destructiva.
  // Cuando hay id, el endpoint correcto es `/replace`: cierra la vigencia de la
  // anterior el dia antes de que arranque la nueva, en la MISMA transaccion.
  // Un POST suelto dejaria dos reglas solapadas pisando el mismo hecho.
  if (payload.data.id) {
    return api.post(`/masters/treatment-rules/${payload.data.id}/replace`, body)
  }
  return api.post("/masters/treatment-rules", body)
}

/* ==========================================================================
   LAS SOCIEDADES Y SU RÉGIMEN TRIBUTARIO

     GET /api/masters/sociedades
     PUT /api/masters/sociedades/{id}/regimen   (body: regimen_tributario, motivo)

   No existía ningún endpoint que escribiera en `entity`. El onboarding marcaba
   el paso «sociedad» en atención por falta de régimen y no había forma de
   darlo: el sistema pedía un dato que no se podía entregar.

   `regimenes_disponibles` sale del RULEPACK del país, no de una lista de la
   SPA. Un campo libre deja escribir «mype» donde el motor espera
   `MYPE_TRIBUTARIO`, y entonces el régimen queda declarado, el onboarding se
   pone en verde y el cálculo sigue yendo por el general sin que nada avise.
   ========================================================================== */

export interface RegimenDisponible {
  codigo: string
  nombre: string | null
  fundamento_legal: string | null
  nota: string | null
}

export interface SociedadMaestro {
  id: string
  razon_social: string
  tax_id: string
  pais: string
  moneda_funcional: string
  regimen_tributario: string | null
  /** `false` = el cálculo va por el general y nadie lo decidió. */
  regimen_declarado: boolean
  /** Vacío = el rulepack de ese país todavía no los declara. NO es «no hay». */
  regimenes_disponibles: RegimenDisponible[]
}

export function fetchSociedades(): Promise<SociedadMaestro[]> {
  return api.get<SociedadMaestro[]>("/masters/sociedades")
}

/**
 * EL PLAN DEL P&L DEL CLIENTE.
 *
 *   GET   /api/masters/plan-gerencial        ?incluir_inactivas
 *   POST  /api/masters/plan-gerencial
 *   PATCH /api/masters/plan-gerencial/{id}
 *
 * `account_local` es normativo; esto NO lo exige ningun fisco. Se disena para
 * contestar las preguntas del negocio —margen bruto, EBITDA, cuanto es caja— y
 * por eso cambia con el negocio. Era la Fase 2 que nunca se construyo: el
 * cliente se quedaba con el plan de la semilla.
 */
export function fetchPlanGerencial(incluirInactivas = false): Promise<PlanGerencial> {
  return api.get<PlanGerencial>("/masters/plan-gerencial", {
    query: { incluir_inactivas: incluirInactivas ? "true" : undefined },
  })
}

export function crearCuentaGerencial(payload: {
  codigo: string
  nombre: string
  tipo_estado: string
  padre_codigo: string | null
  ebitda_incluido: boolean | null
  orden: number | null
  motivo: string
}): Promise<{ id: string; codigo: string; seccion: string }> {
  return api.post("/masters/plan-gerencial", payload)
}

export function editarCuentaGerencial(
  cuentaId: string,
  payload: CambioCuenta & { motivo: string },
): Promise<{ id: string; codigo: string; actualizado: string[]; aviso: string | null }> {
  return api.patch(`/masters/plan-gerencial/${cuentaId}`, payload)
}

/**
 * LOS PERIODOS CONTABLES.
 *
 *   GET  /api/masters/periodos    ?entity_id
 *   POST /api/masters/periodos
 *
 * `faltantes` sale de los COMPROBANTES cargados y no de un rango escrito a
 * mano: son los meses que tienen documentos y ningun periodo donde asentarlos.
 * Es la lista que convierte «el balance esta raro» en «faltan estos seis».
 */
export interface Periodo {
  id: string
  entity_id: string
  razon_social: string
  pais: string
  anio: number
  mes: number
  periodo: string
  estado: "abierto" | "en_revision" | "presentado" | "cerrado"
  asientos: number
  comprobantes: number
}

export interface PeriodoFaltante {
  entity_id: string
  razon_social: string
  pais: string
  periodo: string
  comprobantes: number
}

export function fetchPeriodos(entityId?: string): Promise<{
  periodos: Periodo[]
  faltantes: PeriodoFaltante[]
  nota: string
}> {
  return api.get("/masters/periodos", { query: { entity_id: entityId || undefined } })
}

export function abrirPeriodos(payload: {
  entity_id: string
  desde?: string
  hasta?: string
  solo_faltantes?: boolean
  motivo: string
}): Promise<{
  abiertos: number
  ya_existian: number
  ya_cerrados: number
  periodos: string[]
  cerrados: { periodo: string; estado: string }[]
  nota: string | null
}> {
  return api.post("/masters/periodos", payload)
}

/**
 * Los paises que el sistema sabe operar, y HASTA DONDE llega en cada uno.
 *
 * El alta de sociedad lo consume por una razon concreta: el pais decide el
 * rulepack, el plan de cuentas y el calendario, y **no se puede cambiar
 * despues**. Un desplegable con los cuatro paises escritos aqui a mano se
 * queda viejo el dia que entre el quinto, y ofrecer uno que no tiene rulepack
 * da una sociedad que no calcula ni una obligacion con aspecto de estar bien.
 *
 * `madurez` se muestra en el formulario porque existir y estar COMPLETO no son
 * lo mismo: Peru genera declaraciones, los otros tres son modelo —normativa,
 * calendario y planilla, sin formularios— y quien da de alta una sociedad
 * mexicana tiene que saberlo antes, no en el cierre.
 */
export interface PaisSoportado {
  pais: string
  madurez: string
  moneda?: string
  genera_declaraciones?: boolean
  lee_comprobantes?: boolean
}

export function fetchPaises(): Promise<PaisSoportado[]> {
  return api
    .get<{ paises: PaisSoportado[] }>("/paises")
    .then((r) => (r.paises ?? []).filter((p) => p.madurez !== "ERROR"))
}

/**
 * Da de alta una sociedad, CON SUS LIBROS.
 *
 * Hasta hoy las sociedades solo entraban por `seeds/sociedades.py`, un script
 * con las cuatro de ESTE cliente escritas dentro: un cliente nuevo no podia ni
 * empezar. El backend crea los tres libros en la misma transaccion —una
 * sociedad sin libros se ve normal en la lista y revienta en el primer
 * comprobante, con un error que apunta al comprobante— y valida el pais contra
 * los rulepacks: uno que el sistema no modela no calcula ni una obligacion.
 */
export function crearSociedad(payload: {
  razon_social: string
  tax_id: string
  pais: string
  moneda_funcional: string
  regimen_tributario?: string | null
  motivo: string
}): Promise<{
  id: string
  razon_social: string
  pais: string
  libros_creados: string[]
  siguiente: string
}> {
  return api.post("/masters/sociedades", payload)
}

export function declararRegimen(
  entityId: string,
  payload: { regimen_tributario: string; motivo: string },
): Promise<{
  id: string
  regimen_tributario: string
  anterior: string | null
  validado_contra_rulepack: boolean
  aviso: string | null
}> {
  return api.put(`/masters/sociedades/${entityId}/regimen`, payload)
}
